/**
 * InsertRenderer — render for INSERT statements. Dispatches between the
 * VALUES-form (column map + rows grid), the INSERT…SELECT form (column map +
 * nested SelectRenderer source), and an honest read-only notice for a body
 * keyword VQE doesn't recognize (`bodyKind === 'unknown'`, e.g. MSSQL's
 * `OUTPUT` clause — decision 2, §2.4 S6). Optional clauses RETURNING, ON
 * CONFLICT (PG), and ON DUPLICATE KEY UPDATE (MySQL) render as additional
 * cards.
 *
 * The column map (`InsertColumnMap`) is editContext-aware — VALUES-form column
 * add/remove ops are live once `editContext` + a schema snapshot are ready
 * (§2.4 S6, Task 2); the VALUES rows grid's cells are similarly live-editable.
 * The structured-tail cards (ON CONFLICT / ON DUPLICATE / RETURNING) are also
 * editContext-aware (§2.4 S6b, Task 6). Without an editContext everything
 * still follows the § 1.4 disabled-scaffold hand-off contract.
 *
 * If parsing fails (no target), renders a small fallback
 * surface — the Visual view still offers Edit as SQL.
 */

import { useMemo } from 'react';
import { Maximize2 } from 'lucide-react';

import type { ParseOptions, StatementInfo } from '../../../../sql-grammar/model';
import { Button } from '../../../../../components/ui/button';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { VqeEditContext } from '../../edit/editContext';
import { deriveSubqueryEditContext } from '../../edit/deriveSubqueryEditContext';
import { useVqeStore } from '../../store/vqeStore';
import type { DrillEntry } from '../../types';
import { DmlParseErrorPanel } from '../fallback/DmlParseErrorPanel';
import { makeNestedSubqueryStatement } from '../select/nestedStatement';
import { SelectRenderer } from '../select/SelectRenderer';
import { ClauseCard } from '../select/ClauseCard';
import { VqeTooltip } from '../common/VqeTooltip';
import { vqeCopy } from '../../ux/vqeCopy';
import { extractInsertOutline } from './extractInsertOutline';
import { InsertColumnMap } from './InsertColumnMap';
import { InsertSelectMapping } from './InsertSelectMapping';
import { OnConflictCard } from './OnConflictCard';
import { OnDuplicateCard } from './OnDuplicateCard';
import { ReturningCard } from './ReturningCard';
import { ValuesRowsGrid } from './ValuesRowsGrid';
import { FULL_CANVAS_ROOT_CLASS } from '../common/canvasLayout';

export interface InsertRendererProps {
  readonly statement: StatementInfo;
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly onEditAsSql: () => void;
  readonly drillTabId?: string;
  readonly editContext?: VqeEditContext;
}

export function InsertRenderer({
  statement,
  bufferText,
  options,
  onEditAsSql,
  drillTabId,
  editContext,
}: InsertRendererProps) {
  const { track } = useAnalytics();
  const pushDrill = useVqeStore((s) => s.pushDrill);
  const outline = extractInsertOutline(bufferText, statement.range, options);

  // Derived child editContext for the inline source-SELECT mount — same
  // technique `SubquerySection` uses for chip-mode bodies (`deriveSubqueryEditContext`).
  // Wrapped in try/catch: the document may not be open in the resolver service
  // yet on first render (open is async), which would make `resolveScope` throw;
  // fall back to `undefined` (read-only) rather than crash the renderer.
  const inlineSourceEditContext = useMemo(() => {
    if (!editContext || outline.bodyKind !== 'select') return undefined;
    if (outline.selectSourceStart === null || outline.selectSourceEnd === null) return undefined;
    try {
      return deriveSubqueryEditContext(
        editContext,
        statement.id,
        outline.selectSourceStart,
        outline.selectSourceEnd,
      );
    } catch {
      return undefined;
    }
  }, [editContext, outline.bodyKind, outline.selectSourceStart, outline.selectSourceEnd, statement.id]);

  if (!outline.hasInsert || outline.targetText === null) {
    return <DmlParseErrorPanel kind="insert" onEditAsSql={onEditAsSql} />;
  }

  return (
    <div
      className={FULL_CANVAS_ROOT_CLASS}
      data-testid="vqe-insert-renderer"
      data-body-kind={outline.bodyKind}
      data-has-returning={outline.returningStart !== null ? 'true' : 'false'}
      data-has-on-conflict={outline.onConflictStart !== null ? 'true' : 'false'}
      data-has-on-duplicate={outline.onDuplicateStart !== null ? 'true' : 'false'}
    >
      <InsertColumnMap
        target={outline.targetText}
        columns={outline.columns}
        editContext={editContext}
        bodyKind={outline.bodyKind}
        valuesRowCount={outline.valuesRows.length}
      />

      {outline.bodyKind === 'unknown' ? (
        <ClauseCard title="Values" testId="vqe-insert-unknown-body">
          <div className="flex flex-col items-start gap-2">
            <p className="text-xs italic text-muted-foreground" data-testid="vqe-insert-unknown-body-message">
              {vqeCopy.disabled.insertBodyUnknown}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onEditAsSql}
              data-testid="vqe-insert-unknown-body-edit-as-sql"
            >
              {vqeCopy.actions.editAsSql}
            </Button>
          </div>
        </ClauseCard>
      ) : null}

      {outline.bodyKind === 'values' ? (
        <ValuesRowsGrid
          rows={outline.valuesRows}
          columns={outline.columns}
          editContext={editContext}
          engine={options.engine}
        />
      ) : null}

      {outline.bodyKind === 'select' &&
      outline.selectSourceStart !== null &&
      outline.selectSourceEnd !== null ? (
        <>
          <InsertSelectMapping
            targetColumns={outline.columns}
            sourceText={bufferText.slice(outline.selectSourceStart, outline.selectSourceEnd)}
            options={options}
          />
          <ClauseCard
            title="Source query"
            testId="vqe-insert-source-query"
            accessory={
              <VqeTooltip content="Focus on the source query">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (outline.selectSourceStart === null || outline.selectSourceEnd === null) return;
                    const entry: DrillEntry = {
                      tabId: drillTabId ?? statement.id,
                      parentStatementId: statement.id,
                      bodyStart: outline.selectSourceStart,
                      bodyEnd: outline.selectSourceEnd,
                      label: 'INSERT source',
                      bodyShape: 'insert-source',
                    };
                    track('feature:vqe_drill', {
                      depth: useVqeStore.getState().drillStack.length + 1,
                    });
                    pushDrill(entry);
                  }}
                  className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
                  data-testid="vqe-insert-source-query-focus"
                  aria-label="Focus on the source query"
                >
                  <Maximize2 className="h-3 w-3" />
                  focus
                </Button>
              </VqeTooltip>
            }
          >
            <SelectRenderer
              statement={makeNestedSubqueryStatement(
                statement.id,
                outline.selectSourceStart,
                outline.selectSourceEnd,
              )}
              bufferText={bufferText}
              options={options}
              onEditAsSql={onEditAsSql}
              drillTabId={drillTabId}
              editContext={inlineSourceEditContext}
            />
          </ClauseCard>
        </>
      ) : null}

      {outline.bodyKind === 'default-values' ? (
        <ClauseCard title="Values" testId="vqe-insert-default-values">
          <p className="text-xs italic text-muted-foreground">
            DEFAULT VALUES — uses each column's default.
          </p>
        </ClauseCard>
      ) : null}

      {/* Expansion scope = the tab id, NOT statement.id: statement ids are
          content-derived, so keying expansion on one would collapse these
          cards after every commit made from their own expanded bodies. One
          statement renders per tab, so the tab id can't collide; the
          statement-id fallback only serves direct mounts without a tab. */}
      {outline.onConflictStart !== null && outline.onConflictEnd !== null ? (
        <OnConflictCard
          bufferText={bufferText}
          start={outline.onConflictStart}
          end={outline.onConflictEnd}
          options={options}
          expansionScope={drillTabId ?? statement.id}
          editContext={editContext}
        />
      ) : null}

      {outline.onDuplicateStart !== null && outline.onDuplicateEnd !== null ? (
        <OnDuplicateCard
          bufferText={bufferText}
          start={outline.onDuplicateStart}
          end={outline.onDuplicateEnd}
          options={options}
          expansionScope={drillTabId ?? statement.id}
          editContext={editContext}
        />
      ) : null}

      {outline.returningStart !== null && outline.returningEnd !== null ? (
        <ReturningCard
          bufferText={bufferText}
          start={outline.returningStart}
          end={outline.returningEnd}
          options={options}
          editContext={editContext}
        />
      ) : null}
    </div>
  );
}
